import { DocumentRenderer as Doc } from '@keystone-6/document-renderer';
import { ComponentProps, useState } from 'react';
import { headingRenderer } from './renderers/heading';
import { listRenderer } from './renderers/list';
import { paragraphRenderer } from './renderers/paragraph';
import { tableRenderer } from './renderers/table';

interface DocumentRendererProps {
	document: any;
	components?: ComponentProps<typeof Doc>['componentBlocks'];
	renderers?: ComponentProps<typeof Doc>['renderers'];
}
export type CustomRendererProps = ComponentProps<typeof Doc>;
export type CustomRenderers = NonNullable<CustomRendererProps['renderers']>;

const defaultElementRenderers: CustomRenderers = {
	block: {
		list: listRenderer,
		paragraph: paragraphRenderer,
		heading: headingRenderer,
	},
};

const customComponentRenderers: CustomRendererProps['componentBlocks'] = {
	Table: tableRenderer,
	ImageBlock: props => {
		const { image, altText } = props.image?.data || {};
		if (!image) return null;
		return (
			<div className="flex justify-center">
				<img loading="lazy" src={image?.url} alt={altText || ''} className={'object-cover'} />
			</div>
		);
	},
	VideoBlock: props => {
		const video = props.video?.data.video;
		if (!video) return null;
		return (
			<div className="flex justify-center">
				<video controls>
					<source src={video?.url} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			</div>
		);
	},
	QuoteBlock: props => {
		const { content, attribution } = props || {};
		if (!content) return null;
		return (
			<div className="items-center">
				<div className="max-w-xl mx-auto p-4 border-l-4">
					<p className="text-xl font-semibold italic">{content}</p>
					<p className="text-base mt-4">- {attribution}</p>
				</div>
			</div>
		);
	},
	DropdownBlock: (props?: { items: Array<{ heading: string; content: string }> }) => {
		const DropdownItem = ({ heading, content }: { heading: string; content: string }) => {
			const [isOpen, setIsOpen] = useState(false);

			return (
				<div className="border-t border-b py-2">
					<button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full text-left">
						<span className="text-blue-600 text-lg font-semibold">{heading}</span>
						<svg
							viewBox="0 0 20 20"
							fill="currentColor"
							className={`w-6 h-6 text-blue-600 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
						>
							<path
								fillRule="evenodd"
								d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
								clipRule="evenodd"
							/>
						</svg>
					</button>
					{isOpen && <div className="mt-2 text-base">{content}</div>}
				</div>
			);
		};
		const { items } = props || {};
		return (
			<div className="flex flex-col">
				{items?.map((item, i) => {
					return <DropdownItem key={i} heading={item.heading} content={item.content} />;
				})}
			</div>
		);
	},
};

export function DocumentRenderer(props: DocumentRendererProps) {
	return <Doc renderers={defaultElementRenderers} componentBlocks={customComponentRenderers} {...props} />;
}
